import { formatBalanceUnitSuffix } from './leaveBalanceUtils';
import { formatMoney, formatNumber } from './leaveFormatters';

const LEAVE_LEDGER_EVENT_LABELS = {
  accrual: 'employeeLeave_ledger_event_accrual',
  'initial-grant': 'employeeLeave_ledger_event_initialGrant',
  'admin-adjust': 'employeeLeave_ledger_event_adminAdjust',
  'leave-deduction': 'employeeLeave_ledger_event_leaveDeduction',
  'payroll-bank': 'employeeLeave_ledger_event_payrollBank',
  carryover: 'employeeLeave_ledger_event_carryover',
  'special-day-accrual': 'employeeLeave_ledger_event_specialDayAccrual',
  forfeit: 'employeeLeave_ledger_event_forfeit',
  'daily-accrual': 'employeeLeave_ledger_event_dailyAccrual'
};

// metadata.source values come from giovanni's LeaveEventReason enum. Mapped to
// translation keys so the ledger "Reason" column shows a human label instead of
// the raw kebab tag (e.g. 'carryover-cap-overflow-forfeit').
const LEAVE_EVENT_REASON_LABELS = {
  'auto-earned-cap-overflow-forfeit': 'employeeLeave_ledger_reason_autoEarnedCapOverflowForfeit',
  'cancel-leave-record': 'employeeLeave_ledger_reason_cancelLeaveRecord',
  'cap-payout-overage': 'employeeLeave_ledger_reason_capPayoutOverage',
  'carryover-cap-overflow-forfeit': 'employeeLeave_ledger_reason_carryoverCapOverflowForfeit',
  'carryover-expiry-forfeit': 'employeeLeave_ledger_reason_carryoverExpiryForfeit',
  'carryover-expiry-payout': 'employeeLeave_ledger_reason_carryoverExpiryPayout',
  'contract-change': 'employeeLeave_ledger_reason_contractChange',
  'contract-change-forfeit': 'employeeLeave_ledger_reason_contractChangeForfeit',
  'contract-change-payout': 'employeeLeave_ledger_reason_contractChangePayout',
  'eligibility-backfill': 'employeeLeave_ledger_reason_eligibilityBackfill',
  'first-shift-grant-retroactive': 'employeeLeave_ledger_reason_firstShiftGrantRetroactive',
  'flat-annual-year-start': 'employeeLeave_ledger_reason_flatAnnualYearStart',
  'forfeit-on-termination': 'employeeLeave_ledger_reason_forfeitOnTermination',
  'initial-grant-amount-delta': 'employeeLeave_ledger_reason_initialGrantAmountDelta',
  'leave-contract-update': 'employeeLeave_ledger_reason_leaveContractUpdate',
  'manual-year-reset-carryover': 'employeeLeave_ledger_reason_manualYearResetCarryover',
  'manual-year-reset-forfeit': 'employeeLeave_ledger_reason_manualYearResetForfeit',
  'manual-year-reset-payout': 'employeeLeave_ledger_reason_manualYearResetPayout',
  'payout-all-balances': 'employeeLeave_ledger_reason_payoutAllBalances',
  'termination-forfeit': 'employeeLeave_ledger_reason_terminationForfeit',
  'termination-payout': 'employeeLeave_ledger_reason_terminationPayout',
  'termination-pre-activation-forfeit': 'employeeLeave_ledger_reason_terminationPreActivationForfeit',
  'toil-period-end-settle': 'employeeLeave_ledger_reason_toilPeriodEndSettle',
  'year-end-carryover': 'employeeLeave_ledger_reason_yearEndCarryover',
  'year-end-payout-unused': 'employeeLeave_ledger_reason_yearEndPayoutUnused'
};

// Free-form admin note (metadata.reason set via AdjustLeaveAllowance) wins;
// otherwise translate metadata.source via the map; else fall back to empty.
export const resolveLedgerReason = (metadata, t) => {
  if (!metadata) return '';
  if (typeof metadata.reason === 'string' && metadata.reason.trim()) return metadata.reason.trim();
  const sourceKey = LEAVE_EVENT_REASON_LABELS[metadata.source];
  return sourceKey ? t(sourceKey) : '';
};

export const resolveLedgerEventType = ({ eventType, metadata }, t) => {
  const labelKey = LEAVE_LEDGER_EVENT_LABELS[eventType];
  const baseLabel = labelKey ? t(labelKey) : eventType || '-';
  if (eventType === 'special-day-accrual' && metadata?.holidayName) return `${baseLabel} · ${metadata.holidayName}`;
  return baseLabel;
};

export const formatLedgerAmount = (value, unit, formattingMap, t) => {
  if (unit === 'money') {
    const formatted = formatMoney(formattingMap, Math.abs(value));
    return value < 0 ? `-${formatted}` : `+${formatted}`;
  }
  const suffix = formatBalanceUnitSuffix({ unit }, t);
  const magnitude = formatNumber(Math.abs(value));
  if (value > 0) return `+${magnitude}${suffix}`;
  if (value < 0) return `-${magnitude}${suffix}`;
  return `0${suffix}`;
};
